import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { meetingService, Meeting } from '../../services/meetingService';
import { formatWallClockDate } from '../../utils/dateUtils';
import { SummaryRecipientsDialog, SummaryRecipients } from './SummaryRecipientsDialog';
import { patientLabel } from '../../utils/patientLabel';

interface CMSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: Meeting;
  onSuccess?: () => void;
}

export const CMSummaryModal: React.FC<CMSummaryModalProps> = ({
  isOpen,
  onClose,
  meeting,
  onSuccess,
}) => {
  const [summaryText, setSummaryText] = useState('');
  const [originalSummaryText, setOriginalSummaryText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    meetingService.getCMSummary(meeting.id)
      .then(({ content }) => {
        setSummaryText(content);
        setOriginalSummaryText(content);
      })
      .catch(() => toast.error('Failed to load CM summary'))
      .finally(() => setIsLoading(false));
  }, [isOpen, meeting.id]);

  const hasChanged = summaryText !== originalSummaryText;

  // Preview the rendered PDF - the same thing the patient receives, built from
  // the summary type's pdf_layout rather than from the plain text above.
  const handlePreviewPdf = useCallback(async () => {
    setIsGeneratingPdf(true);
    try {
      const blob = await meetingService.generateSummaryPdf(meeting.id);
      setPdfPreviewUrl(window.URL.createObjectURL(blob));
    } catch (err) {
      console.error('Failed to generate PDF', err);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [meeting.id]);

  const handleClosePdfPreview = useCallback(() => {
    if (pdfPreviewUrl) {
      // Revoke before dropping the reference, or the blob leaks for the life
      // of the tab.
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  }, [pdfPreviewUrl]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await meetingService.updateCMSummary(meeting.id, summaryText);
      setOriginalSummaryText(summaryText);
      toast.success('Summary saved successfully');
      onSuccess?.();
    } catch {
      toast.error('Failed to save summary');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Save, then choose recipients, then send - the same order the PSY/ADHD
   * summary uses. Saving first means what goes out is always what is on screen.
   *
   * The save passes approve=true: there is no separate Approve button any more,
   * and sending a summary to a patient IS the approval of it. Without this the
   * meeting would keep summary_approved=false even after the text had gone out,
   * which reads as "never approved" everywhere that flag is shown or filtered on.
   */
  const openRecipients = async () => {
    setIsSaving(true);
    try {
      await meetingService.updateCMSummary(meeting.id, summaryText, true);
      setOriginalSummaryText(summaryText);
      onSuccess?.();
    } catch {
      toast.error('Failed to save the summary');
      return;
    } finally {
      setIsSaving(false);
    }
    setShowRecipients(true);
  };

  const handleSend = async (recipients: SummaryRecipients) => {
    setIsSending(true);
    try {
      await meetingService.sendSummary(meeting.id, recipients);
      toast.success(recipients.gpEmail ? 'Summary sent to patient and GP' : 'Summary sent');
      setShowRecipients(false);
      onSuccess?.();
      handleClose(true);
    } catch {
      toast.error('Failed to send the summary');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = (force = false) => {
    if (!force && hasChanged) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    }
    setSummaryText('');
    setOriginalSummaryText('');
    onClose();
  };

  if (!isOpen) return null;

  const meetingDate = formatWallClockDate(meeting.scheduled_at);
  const meetingTypeName = meeting.meeting_type?.name ?? 'CM Meeting';
  // This modal serves both case-manager and titration summaries. Only the
  // titration ones go out to anybody: a CM summary is an internal record, so it
  // is saved here and never sent.
  const meetingTypeKind = meeting.meeting_type?.type ?? '';
  const isCMMeeting = meetingTypeKind.startsWith('case_manager');
  const isTitrationMeeting = meetingTypeKind.startsWith('titration');
  // Titration meeting types are deliberately NOT linked to a summary type -
  // resolve_summary_type_for_meeting() on the backend looks theirs up by kind
  // instead ('titration_followup', falling back to 'titration'). So checking
  // meeting_type.summary_type here is always null for them, which is why the
  // preview button never appeared. They always have a layout to render.
  const hasPdfLayout =
    isTitrationMeeting || !!meeting.meeting_type?.summary_type?.pdf_layout?.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1400] p-4">
      <div
        className="bg-[#B4D9D4] rounded-3xl px-8 pb-8 w-full shadow-2xl relative max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative pt-3 pb-3">
          <div className="flex items-center justify-center">
            <h2 className="text-2xl font-bold text-[#366A75]">{meetingTypeName} Summary</h2>
            <button
              onClick={() => handleClose()}
              className="absolute right-0 text-[#366A75] hover:text-[#2D5A60] transition-colors disabled:opacity-50"
              disabled={isSaving}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 pb-3">
          {/* Meeting info */}
          <div className="flex items-center gap-2 text-sm text-[#366A75] mb-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            <span>{meetingDate}</span>
            <span className="text-[#366A75]/60">·</span>
            <span>{patientLabel(meeting)}</span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#366A75]" />
            </div>
          ) : (
            <textarea
              value={summaryText}
              onChange={(e) => setSummaryText(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent h-[460px] resize-none"
              placeholder="No summary content available"
              disabled={isSaving}
              style={{ fontFamily: 'Arial, sans-serif', fontSize: '13px', lineHeight: '1.6', color: '#366A75' }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-1 flex-shrink-0">
          {/* Left: PDF preview */}
          <div className="flex items-center">
            {hasPdfLayout && (
              <button
                type="button"
                onClick={handlePreviewPdf}
                disabled={isSaving || isLoading || isSending || isGeneratingPdf}
                title="Preview PDF"
                className="p-2 text-[#3A7380] hover:text-[#2D5A60] hover:bg-[#B8DDD0]/60 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isGeneratingPdf ? (
                  <div className="w-5 h-5 border-2 border-[#3A7380] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            )}
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving || isLoading || isSending || !hasChanged}
              className="px-7 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            {!isCMMeeting && (
              <button
                onClick={openRecipients}
                disabled={isSaving || isLoading || isSending}
                className="px-7 py-2 bg-[#3A7380] text-white rounded-xl shadow-sm hover:bg-[#2D5A60] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                {isSending ? 'Sending...' : meeting.summary_sent ? 'Send Again' : 'Send'}
              </button>
            )}
            <button
              onClick={() => handleClose()}
              disabled={isSaving || isSending}
              className="px-7 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* PDF Preview */}
      {pdfPreviewUrl && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1410] p-6"
          onClick={handleClosePdfPreview}
        >
          <div
            className="bg-[#B8DDD0] rounded-3xl p-4 w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClosePdfPreview}
              className="absolute top-4 right-4 z-10 text-[#366A75] hover:text-[#2D5A60] transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <iframe
              src={pdfPreviewUrl}
              title="Summary PDF"
              className="w-full flex-1 rounded-2xl border-4 border-white/60"
              style={{ minHeight: 0 }}
            />
          </div>
        </div>
      )}

      <SummaryRecipientsDialog
        isOpen={showRecipients}
        meeting={meeting}
        busy={isSending}
        onCancel={() => setShowRecipients(false)}
        onConfirm={handleSend}
      />
    </div>
  );
};
